/**
 * Tree manipulation utility functions for dendrogram components
 * Handles expand/collapse logic and tree state management
 */

import {TreeNode} from './types';

/**
 * Recursively collapses nodes beyond a specified depth
 * depth 0 = root, depth 1 = first level, depth 2 = second level, etc.
 * maxDepth=2 means show levels 0, 1, 2 (total of 3 visible levels)
 *
 * @param d - The tree node to process
 * @param maxDepth - Maximum depth to keep expanded (nodes at this depth or beyond will be collapsed)
 */
export const collapseAfterDepth = (d: TreeNode, maxDepth: number): void => {
    // Collapse nodes at or beyond maxDepth that have children
    if (d.depth >= maxDepth && d.children) {
        d._children = d.children;
        d.children = undefined;
    } else if (d.children) {
        // Recursively process children if not collapsed
        d.children.forEach(child => collapseAfterDepth(child as TreeNode, maxDepth));
    }
};

/**
 * Toggles the expand/collapse state of a tree node
 * When expanding, only reveals immediate children (they start collapsed)
 * When collapsing, hides all children
 *
 * @param d - The tree node to toggle
 * @returns The updated node
 */
export const toggleChildren = (d: TreeNode): TreeNode => {
    if (d.children) {
        // Collapse: hide children
        d._children = d.children;
        d.children = undefined;
    } else if (d._children) {
        // Expand: show immediate children only
        d.children = d._children;
        d._children = undefined;

        // Ensure newly revealed children start collapsed if they have children
        if (d.children) {
            d.children.forEach((child: any) => {
                const childNode = child as TreeNode;
                if (childNode.children && childNode.children.length > 0) {
                    childNode._children = childNode.children;
                    childNode.children = undefined;
                }
            });
        }
    }
    return d;
};

/**
 * Finds a node in the tree by its data ID
 *
 * @param current - The current node to search from
 * @param targetId - The ID to search for
 * @returns The found node or null
 */
export const findNodeById = (current: TreeNode, targetId: number): TreeNode | null => {
    if (current.data.id === targetId) return current;

    if (current.children) {
        for (const child of current.children) {
            const found = findNodeById(child as TreeNode, targetId);
            if (found) return found;
        }
    }

    if (current._children) {
        for (const child of current._children) {
            const found = findNodeById(child as TreeNode, targetId);
            if (found) return found;
        }
    }

    return null;
};

/**
 * Finds the first mapped node (node with minimum depth) from a set of mapped node IDs
 * This is useful for centering the dendrogram on the topmost mapped node in the hierarchy
 *
 * @param root - The root of the tree
 * @param mappedNodeIds - Set of node IDs that are mapped
 * @returns The ID of the first mapped node, or null if none found
 */
export const findFirstMappedNode = (root: TreeNode, mappedNodeIds: Set<number>): number | null => {
    let firstMappedNodeId: number | null = null;
    let minDepth: number = Infinity;

    // Traverse tree and find mapped node with minimum depth
    const traverse = (node: TreeNode): void => {
        if (mappedNodeIds.has(node.data.id) && node.depth < minDepth) {
            minDepth = node.depth;
            firstMappedNodeId = node.data.id;
        }

        if (node.children) {
            node.children.forEach((child) => traverse(child as TreeNode));
        }

        if (node._children) {
            node._children.forEach((child) => traverse(child as TreeNode));
        }
    };

    traverse(root);

    return firstMappedNodeId;
};
